import { Alert, Button, Checkbox, Input, Modal, Space, Table } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuid } from "uuid";
import { Prompt } from "react-router-dom";
import { ScootStatusToKorean } from "../../../utils/Translate";
import {
  getWarehouseContext,
  stockOutVehicle,
  WarehouseContext,
  WarehouseStockOutRequest,
} from "../service/warehouse.service";

export const MAX_BULK_STOCK_OUT = 200;
const CONCURRENCY = 3;
type RowState = "loading" | "ready" | "blocked" | "waiting" | "running" | "success" | "failed";
interface Row {
  id: string;
  context?: WarehouseContext;
  state: RowState;
  detail?: string;
}

interface Props {
  vehicleIds: string[];
  onClose: () => void;
  onChanged: () => void;
}

const errorMessage = (error: any) => error?.response?.data?.message || "응답을 확인하지 못했습니다";
const visibleIssues = (context: WarehouseContext) => context.issues.filter(
  (issue) => issue.code !== "NO_SELECTABLE_STOCK" && issue.code !== "DEER_CDC_AUTHORITATIVE"
);
const suggestedStocks = (context: WarehouseContext) => context.stocks.filter(
  (stock) => stock.selectable && context.suggestedStockIds.includes(stock.stockId)
);
const eligible = (context: WarehouseContext) => context.canStockOut && !context.riding && context.assetNo != null &&
  (!context.stocks.some((stock) => stock.selectable) || suggestedStocks(context).length > 0);
const label: Record<RowState, string> = {
  loading: "조회 중",
  ready: "출고 가능",
  blocked: "대상 제외",
  waiting: "요청 대기",
  running: "처리 중",
  success: "출고 완료",
  failed: "확인 필요",
};

async function processQueue<T>(
  items: T[],
  action: (item: T) => Promise<void>,
  cancelled: () => boolean
) {
  let next = 0;
  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, items.length) }, async () => {
    while (!cancelled() && next < items.length) {
      const item = items[next++];
      await action(item);
    }
  }));
}

export function VehicleBulkStockOutModal({ vehicleIds, onClose, onChanged }: Props) {
  const vehicleKey = JSON.stringify([...new Set(vehicleIds)]);
  const [rows, setRows] = useState<Row[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [phase, setPhase] = useState<"loading" | "review" | "running" | "results">("loading");
  const [reason, setReason] = useState("");
  const [physicalConfirmed, setPhysicalConfirmed] = useState(false);
  const [correctionConfirmed, setCorrectionConfirmed] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [stopping, setStopping] = useState(false);
  const generation = useRef(0);
  const busy = useRef(false);
  const executing = useRef(false);
  const stopRequested = useRef(false);

  const loadRows = useCallback(async (ids: string[], retry = false) => {
    if (busy.current) return;
    const current = ++generation.current;
    busy.current = true;
    stopRequested.current = false;
    setPhase("loading");
    setReason("");
    setPhysicalConfirmed(false);
    setCorrectionConfirmed(false);
    setSelectedIds([]);
    setLoadError("");
    if (!ids.length || ids.length > MAX_BULK_STOCK_OUT) {
      setRows([]);
      setLoadError(`출고할 기기를 1대 이상 ${MAX_BULK_STOCK_OUT}대 이하로 선택해 주세요`);
      setPhase("review");
      busy.current = false;
      return;
    }
    const previews: Row[] = ids.map((id) => ({ id, state: "loading" }));
    setRows((previous) => retry
      ? [...previous.filter((row) => !ids.includes(row.id)), ...previews]
      : previews
    );
    const ready: string[] = [];
    await processQueue(ids, async (id) => {
      let row: Row;
      try {
        const context = await getWarehouseContext(id);
        if (context.vehicleId !== id) throw new Error("기기 정보가 변경됐습니다");
        const canStockOut = eligible(context);
        let detail: string | undefined;
        if (!canStockOut) {
          if (context.riding) detail = "탑승 중인 기기입니다";
          else if (context.canStockOut && context.stocks.some((stock) => stock.selectable) && suggestedStocks(context).length === 0) {
            detail = "출고할 재고 선택이 필요합니다. 기기 상세에서 확인해 주세요";
          } else detail = visibleIssues(context).map((issue) => issue.message).join(" · ") || "현재 출고 대상이 아닙니다";
        }
        row = { id, context, state: canStockOut ? "ready" : "blocked", detail };
        if (canStockOut) ready.push(id);
      } catch (error) {
        row = { id, state: "failed", detail: errorMessage(error) };
      }
      if (current === generation.current) {
        setRows((previous) => previous.map((item) => item.id === id ? row : item));
      }
    }, () => current !== generation.current);
    if (current !== generation.current) return;
    busy.current = false;
    setSelectedIds(ready);
    setPhase("review");
  }, []);

  useEffect(() => {
    void loadRows(JSON.parse(vehicleKey));
    return () => {
      generation.current += 1;
      busy.current = false;
      executing.current = false;
    };
  }, [vehicleKey, loadRows]);

  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (!executing.current) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, []);

  const selected = rows.filter((row) =>
    selectedIds.includes(row.id) && row.state === "ready" && row.context && eligible(row.context)
  );
  const needsCorrection = selected.some((row) => row.context?.correctionRequired);
  const valid = phase === "review" && selected.length > 0 &&
    reason.trim().length > 0 && reason.trim().length <= 500 &&
    physicalConfirmed && (!needsCorrection || correctionConfirmed);
  const failedIds = rows.filter((row) => row.state === "failed").map((row) => row.id);
  const working = phase === "loading" || phase === "running";

  async function submit() {
    if (!valid || busy.current) return;
    busy.current = true;
    executing.current = true;
    stopRequested.current = false;
    setStopping(false);
    setPhase("running");
    const current = generation.current;
    // 확인한 조회 버전과 각 기기별 요청 번호를 실행 시작 시 고정함
    const jobs = selected.map((row) => {
      const context = row.context!;
      const stocks = suggestedStocks(context);
      const request: WarehouseStockOutRequest = {
        requestId: uuid(),
        expectedRevision: context.revision,
        assetNo: context.assetNo!,
        stockIds: stocks.map((stock) => stock.stockId),
        expectedHistoryIds: [...new Set(stocks.flatMap((stock) =>
          stock.matchedHistoryId == null ? [] : [stock.matchedHistoryId]
        ))],
        physicalConfirmed: true,
        correctionConfirmed: context.correctionRequired && correctionConfirmed,
        reason: reason.trim(),
      };
      return { id: row.id, request };
    });
    setRows((previous) => previous.map((row) => jobs.some((job) => job.id === row.id)
      ? { ...row, state: "waiting", detail: undefined }
      : row
    ));
    const attempted = new Set<string>();
    await processQueue(jobs, async (job) => {
      attempted.add(job.id);
      if (current === generation.current) {
        setRows((previous) => previous.map((row) => row.id === job.id
          ? { ...row, state: "running" }
          : row
        ));
      }
      let result: Pick<Row, "state" | "detail">;
      try {
        const response = await stockOutVehicle(job.id, job.request);
        result = {
          state: "success",
          detail: response.unresolvedHistoryIds.length
            ? `출고 완료 · 미확정 입고 기록 ${response.unresolvedHistoryIds.length}건 보존`
            : "출고 완료",
        };
      } catch (error: any) {
        result = {
          state: "failed",
          detail: error?.response?.status === 409
            ? `${errorMessage(error)} · 현재 상태를 다시 확인해 주세요`
            : `${errorMessage(error)} · 처리됐을 수도 있으므로 다시 조회한 후 확인해 주세요`,
        };
      }
      if (current === generation.current) {
        setRows((previous) => previous.map((row) => row.id === job.id ? { ...row, ...result } : row));
      }
    }, () => current !== generation.current || stopRequested.current);
    if (current !== generation.current) return;
    setRows((previous) => previous.map((row) => row.state === "waiting"
      ? { ...row, state: "failed", detail: "중단돼 출고 요청을 보내지 않았습니다" }
      : row
    ));
    busy.current = false;
    executing.current = false;
    setSelectedIds([]);
    setPhase("results");
    if (attempted.size) onChanged();
  }

  return (
    <Modal
      title="기기 일괄 출고"
      visible
      width={1100}
      onCancel={() => !executing.current && onClose()}
      maskClosable={false}
      closable={phase !== "running"}
      keyboard={phase !== "running"}
      footer={[
        <Button key="close" disabled={phase === "running"} onClick={() => !executing.current && onClose()}>
          닫기
        </Button>,
        phase === "running" && <Button
          key="stop"
          disabled={stopping}
          onClick={() => {
            stopRequested.current = true;
            setStopping(true);
          }}
        >
          남은 요청 중단
        </Button>,
        failedIds.length > 0 && <Button key="retry" disabled={working} onClick={() => loadRows(failedIds, true)}>
          실패만 다시 확인
        </Button>,
        phase !== "results" && <Button
          key="submit"
          type="primary"
          loading={phase === "running"}
          disabled={!valid}
          onClick={submit}
        >
          일괄 출고 확정
        </Button>,
      ]}
    >
      <Prompt when={phase === "running"} message={() => {
        return "이미 전송한 출고 요청은 계속 처리될 수 있습니다. 아직 보내지 않은 요청은 중단합니다. 이 화면을 나가시겠습니까?";
      }} />
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Alert
          type="info"
          message="처리 가능한 기기만 출고합니다. 아래 재고·입고 연결과 실물을 확인해 주세요. 배치 요청은 보내지 않으며, 창을 닫으면 아직 요청하지 않은 기기는 처리되지 않습니다"
        />
        {loadError && <Alert type="error" message={loadError} />}
        {stopping && phase === "running" && <Alert
          type="info"
          message="이미 전송한 요청의 결과를 기다리는 중입니다. 남은 기기에는 요청을 보내지 않습니다"
        />}
        <div aria-live="polite">
          전체 {rows.length}대 · 선택 {selectedIds.length}대 · 출고 완료 {rows.filter((row) => row.state === "success").length}대 · 확인 필요 {failedIds.length}대
        </div>
        <Table<Row>
          size="small"
          rowKey="id"
          dataSource={rows}
          loading={phase === "loading"}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 950 }}
          rowSelection={{
            selectedRowKeys: selectedIds,
            onChange: (keys) => {
              setSelectedIds(keys.map(String));
              setPhysicalConfirmed(false);
              setCorrectionConfirmed(false);
            },
            getCheckboxProps: (row) => ({ disabled: working || row.state !== "ready" }),
          }}
          columns={[
            { title: "기기 QR", render: (_, row) => row.context?.qr || row.id },
            { title: "자산번호", render: (_, row) => row.context?.assetNo ?? "-" },
            {
              title: "현재 상태",
              render: (_, row) => row.context
                ? ScootStatusToKorean[row.context.status] || row.context.status
                : "-",
            },
            {
              title: "출고할 재고 · 연결 입고",
              render: (_, row) => row.context && suggestedStocks(row.context).map((stock) => (
                <div key={stock.stockId}>
                  {stock.campName || `창고 #${stock.campId}`} · 재고 {stock.stockId} / 입고 {stock.matchedHistoryId ?? "미확정"} · 당시 QR {stock.qr}
                </div>
              )),
            },
            {
              title: "처리 방식",
              render: (_, row) => row.state === "blocked" || !row.context
                ? "-"
                : row.context.correctionRequired ? "상태·재고 보정" : "정상 출고",
            },
            {
              title: "결과 · 안내",
              render: (_, row) => (
                <div>
                  {label[row.state]}
                  {row.detail && <div>{row.detail}</div>}
                  {row.context && visibleIssues(row.context).map((issue, index) => (
                    <div key={`${issue.code}-${index}`}>{issue.message}</div>
                  ))}
                </div>
              ),
            },
          ]}
        />
        {phase === "review" && <>
          <Input.TextArea
            aria-label="일괄 출고 사유"
            placeholder="출고 사유 (필수, 최대 500자)"
            maxLength={500}
            rows={2}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          <Checkbox checked={physicalConfirmed} onChange={(event) => setPhysicalConfirmed(event.target.checked)}>
            선택한 모든 기기의 실물과 자산번호를 확인했고 출고를 진행합니다
          </Checkbox>
          {needsCorrection && <>
            <Alert type="warning" message="보정 대상은 선택한 재고만 정리하며, 확정할 수 없는 입고 기록은 보존합니다" />
            <Checkbox checked={correctionConfirmed} onChange={(event) => setCorrectionConfirmed(event.target.checked)}>
              미확정 입고 기록을 보존하고 선택한 기기의 상태·재고를 보정하는 데 동의합니다
            </Checkbox>
          </>}
        </>}
      </Space>
    </Modal>
  );
}
